import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const FavouritesContext = createContext(null);
const key = (kind, id) => `${kind}:${id}`;

export function FavouritesProvider({ children }) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(() => new Set());
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (!user) { setSaved(new Set()); return; }
    api.get("/favourites/ids").then(({ data }) => {
      setSaved(new Set([...(data.recipe || []).map((i) => key("recipe", i)), ...(data.historical || []).map((i) => key("historical", i))]));
    }).catch(() => {});
  }, [user]);

  const toggle = useCallback(async (kind, id, onError) => {
    const k = key(kind, id);
    const on = !saved.has(k);
    try {
      const { data } = on ? await api.put(`/favourites/${kind}/${id}`) : await api.delete(`/favourites/${kind}/${id}`);
      setSaved((s) => { const n = new Set(s); if (on) n.add(k); else n.delete(k); return n; });
      if (data?.favourite_count != null) setCounts((c) => ({ ...c, [k]: data.favourite_count }));
      toast.success(on ? "Saved to Favourites" : "Removed from Favourites");
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't update Favourites";
      toast.error(msg);
      onError?.(msg);
      return false;
    }
  }, [saved]);

  return (
    <FavouritesContext.Provider value={{ isSaved: (kind, id) => saved.has(key(kind, id)), countFor: (kind, id) => counts[key(kind, id)], toggle }}>
      {children}
    </FavouritesContext.Provider>
  );
}

export const useFavourites = () => useContext(FavouritesContext);
